import React from 'react';
import CurrencyInput from 'react-currency-input-field';
import styled, { css } from 'styled-components';
import { StyledBadgeIcon, StyledBadgeText, StyledFieldset, StyledLabel } from 'styled/UI/StyledForm';
import colors from 'theme/colors';
import fonts from 'theme/fonts';
import sizes from 'theme/sizes';
import Icon from './Icon';

const CurrencyInputField = React.forwardRef(({
    value,
    placeholder,
    label,
    layout = 'straight',
    badgeText,
    badgeIcon,
    badgeIconColor,
    badgePosition,
    badgeIconBackground,
    isBadgeClickable,
    disabled,
    textIndent,
    name,
    onChange,
    onBadgeClick,
}, ref) => (
    <StyledFieldset>
        {label && (
            <StyledLabel layout={layout}>
                {label}
            </StyledLabel>
        )}

        <StyledInput
            placeholder={placeholder}
            value={value}
            decimalsLimit={2}
            intlConfig={{ locale: 'pt-BR', currency: 'BRL' }}
            layout={layout}
            textIndent={textIndent}
            ref={ref}
            disabled={disabled}
            onValueChange={onChange}
            name={name}
        />

        {badgeText && (
            <StyledBadgeText
                position={badgePosition}
                clickable={isBadgeClickable && !disabled}
                layout={layout}
                onClick={() => onBadgeClick()}
            >
                {badgeText}
            </StyledBadgeText>
        )}

        {badgeIcon && (
            <StyledBadgeIcon
                id='badge_icon'
                layout={layout}
                bgColor={badgeIconBackground}
                position={badgePosition}
                clickable={isBadgeClickable && !disabled}
                onClick={() => onBadgeClick()}
            >
                <Icon name={badgeIcon} color={badgeIconColor} />
            </StyledBadgeIcon>
        )}

    </StyledFieldset>
));

export default CurrencyInputField;

const StyledInput = styled(CurrencyInput)`
    width: 100%;
    outline-color: transparent;
    transition: all ease 0.15s;
    background: transparent;
    outline: transparent;
    color: ${colors.night};

    ${({ disabled }) => disabled && css`
        pointer-events: none;
    `}

    ${({ icon }) => icon && css`
        padding-left: ${sizes.large} !important;
    `}

    ${({ textIndent }) => textIndent && css`
        text-indent: ${textIndent};
        
        &::placeholder {
            text-indent: ${textIndent};
        }
    `}

    &::placeholder {
        color: ${colors.nightLight};
    }

    ${({ layout }) => layout === 'straight' && css`
        border: none;
        border-bottom: ${sizes.quark} solid ${colors.nightLight};
        padding: ${sizes.quark};
        font-weight: ${fonts.weight.bold};
        
        &::placeholder {
            font-weight: ${fonts.weight.regular};
        }

        &:focus {
            border-bottom: 2px solid ${colors.night}
        }
    `}

        ${({ layout, disabled }) => layout === 'squired' && disabled && css`
        background: transparent;
        border: 1.5px solid ${colors.night}22;
        padding: 11px 14px;
        pointer-events: none;
        border-radius: 10px;
        margin-top: 5px;
        opacity: .8;
        pointer-events: none;
    `}

    ${({ layout }) => layout === 'squired' && css`
        background: ${colors.night}08;
        border: 1.5px solid ${colors.night}22;
        padding: 11px 14px;
        border-radius: 10px;
        margin-top: 5px;

        &:focus {
            border: 1.5px solid ${colors.occasionalPurple};
            box-shadow: 0 0 0 3px ${colors.occasionalPurple}18;
        }
    `}

    ${({ layout }) => layout === 'straight-center' && css`
        border: none;
        border-bottom: ${sizes.quark} solid ${colors.nightLight};
        padding: ${sizes.quark};
        font-weight: ${fonts.weight.bold};
        text-align: center;
        color: ${colors.neutralDark};
        
        &::placeholder {
            font-weight: ${fonts.weight.regular};
            text-align: center;
            color: ${colors.nightLight};
        }

        &:focus {
            border-bottom: 2px solid ${colors.neutralDark}
        }
    `}
   
    ${({ layout }) => layout === 'rounded' && css`
        border: 2px solid #ccc;
        border-radius: 10px;
        padding: ${sizes.mini};
        font-weight: ${fonts.weight.bold};
        background: transparent;
        color: ${colors.nightDark};

        &:focus {
            border: ${sizes.quark} solid ${colors.occasionalPurple};
        }
    `}
`